import { useEffect, useRef, useCallback } from "react";

export interface ContentBlockTimerProps {
  /** Duration in seconds for the current content block */
  durationSeconds: number;

  /** Callback fired when the timer expires */
  onTimerExpire: () => void;

  /** Whether the timer is active */
  isActive?: boolean;

  /** Whether to automatically restart the timer when it expires */
  autoRestart?: boolean;

  /** Callback fired on each tick with remaining and total time in seconds */
  onTick?: (remainingTime: number, totalTime: number) => void;

  /** Tick interval in milliseconds (default: 100ms) */
  tickInterval?: number;

  /** Unique key to force timer restart when block changes */
  blockKey?: string | number | null | undefined;
}

export interface ContentBlockTimerReturn {
  /** Start or restart the timer */
  startTimer: () => void;

  /** Stop the timer */
  stopTimer: () => void;

  /** Reset the timer to the beginning */
  resetTimer: () => void;

  /** Whether the timer is currently running */
  isRunning: boolean;
}

export const useContentBlockTimer = ({
  durationSeconds,
  onTimerExpire,
  isActive = true,
  autoRestart = false,
  onTick,
  tickInterval = 100,
  blockKey,
}: ContentBlockTimerProps): ContentBlockTimerReturn => {
  const mainTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef(false);
  const startTimeRef = useRef<number | null>(null);
  const currentDurationRef = useRef(durationSeconds);
  const currentBlockKeyRef = useRef(blockKey);
  const onTimerExpireRef = useRef(onTimerExpire);
  const onTickRef = useRef(onTick);

  // Update refs to avoid stale closures
  currentDurationRef.current = durationSeconds;
  currentBlockKeyRef.current = blockKey;
  onTimerExpireRef.current = onTimerExpire;
  onTickRef.current = onTick;

  const clearAllTimers = useCallback(() => {
    if (mainTimerRef.current) {
      clearTimeout(mainTimerRef.current);
      mainTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    isRunningRef.current = false;
    startTimeRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    // console.log(
    //   "startTimer called with duration:",
    //   currentDurationRef.current,
    //   "blockKey:",
    //   currentBlockKeyRef.current
    // );

    // Clear any existing timers
    clearAllTimers();

    if (currentDurationRef.current <= 0) {
      //console.log("Duration is 0 or negative, not starting timer");
      return;
    }

    isRunningRef.current = true;
    startTimeRef.current = Date.now();

    // Set up main timer for expiration
    mainTimerRef.current = setTimeout(() => {
      //console.log("Main timer expired for block:", currentBlockKeyRef.current);
      clearAllTimers();

      // Use ref to avoid stale closure
      if (onTimerExpireRef.current) {
        onTimerExpireRef.current();
      }

      if (autoRestart && isActive) {
        //console.log("Auto-restarting timer");
        // Don't immediately restart - let the parent component handle the next block
      }
    }, currentDurationRef.current * 1000);

    // Set up tick timer for progress updates
    if (onTickRef.current) {
      // Call initial tick
      onTickRef.current(currentDurationRef.current, currentDurationRef.current);

      tickTimerRef.current = setInterval(() => {
        if (startTimeRef.current && isRunningRef.current && onTickRef.current) {
          const elapsed = (Date.now() - startTimeRef.current) / 1000;
          const remaining = Math.max(0, currentDurationRef.current - elapsed);

          onTickRef.current(remaining, currentDurationRef.current);

          // Stop if we've reached the end
          if (remaining <= 0) {
            if (tickTimerRef.current) {
              clearInterval(tickTimerRef.current);
              tickTimerRef.current = null;
            }
          }
        }
      }, tickInterval);
    }

    // console.log(
    //   "Timer started successfully for block:",
    //   currentBlockKeyRef.current
    // );
  }, [clearAllTimers, autoRestart, isActive, tickInterval]);

  const stopTimer = useCallback(() => {
    //console.log("stopTimer called for block:", currentBlockKeyRef.current);
    clearAllTimers();
  }, [clearAllTimers]);

  const resetTimer = useCallback(() => {
    //console.log("resetTimer called for block:", currentBlockKeyRef.current);
    const wasRunning = isRunningRef.current;
    clearAllTimers();

    if (wasRunning && isActive) {
      startTimer();
    }
  }, [startTimer, clearAllTimers, isActive]);

  // Auto-start when conditions are met or block changes
  useEffect(() => {
    // console.log("Timer useEffect triggered:", {
    //   isActive,
    //   durationSeconds,
    //   blockKey,
    //   isCurrentlyRunning: isRunningRef.current,
    // });

    if (isActive && durationSeconds > 0) {
      // Always restart timer when blockKey changes or when first becoming active
      startTimer();
    } else {
      stopTimer();
    }

    // Cleanup on unmount or dependency change
    return () => {
      //console.log("Timer useEffect cleanup for block:", blockKey);
      clearAllTimers();
    };
  }, [
    isActive,
    durationSeconds,
    blockKey,
    startTimer,
    stopTimer,
    clearAllTimers,
  ]);

  return {
    startTimer,
    stopTimer,
    resetTimer,
    isRunning: isRunningRef.current,
  };
};
